import { PublicVariables } from "./PublicVariables";

/**
 * Translation feature constants
 * Maps app locale codes to Azure Translator language codes
 */

// Azure Translator supported language codes mapping
export const LOCALE_TO_AZURE_LANGUAGE_MAP = {
    en_AU: "en",
    en_CA: "en",
    en_GB: "en",
    en_US: "en",
    es_ES: "es",
    es_MX: "es",
    es_AR: "es",
    pt_PT: "pt",
    pt_BR: "pt",
    fr_FR: "fr",
    fr_CA: "fr",
    de_DE: "de",
    it_IT: "it",
    zh_CN: "zh-Hans",
    ja_JP: "ja",
    pl_PL: "pl",
};

// Supported languages for translation dropdown (unique languages only)
// Structure matches LanguageConstants.js for consistency
// Sorted alphabetically by languageName to match call topbar language dropdown
export const TRANSLATION_LANGUAGES = [
    {
        code: "de",
        languageName: "Deutsch",
        countryName: "Deutschland",
        localeCode: "de_DE",
        class: "german-flag",
    },
    {
        code: "en",
        languageName: "English",
        countryName: "United States",
        localeCode: "en_US",
        class: "us-flag",
    },
    {
        code: "es",
        languageName: "Español",
        countryName: "España",
        localeCode: "es_ES",
        class: "espana-flag",
    },
    {
        code: "fr",
        languageName: "Français",
        countryName: "France",
        localeCode: "fr_FR",
        class: "france-flag",
    },
    {
        code: "it",
        languageName: "Italian",
        countryName: "Italy",
        localeCode: "it_IT",
        class: "italy-flag",
    },
    {
        code: "ja",
        languageName: "日本",
        countryName: "Japan",
        localeCode: "ja_JP",
        class: "japan-flag",
    },
    {
        code: "pl",
        languageName: "Polski",
        countryName: "Poland",
        localeCode: "pl_PL",
        class: "poland-flag",
    },
    {
        code: "pt",
        languageName: "Português",
        countryName: "Brasil",
        localeCode: "pt_BR",
        class: "brasil-flag",
    },
    {
        code: "zh-Hans",
        languageName: "中国人",
        countryName: "China",
        localeCode: "zh_CN",
        class: "china-flag",
    },
];

// Languages for agent "Send message in" dropdown (sorted alphabetically by English name)
// Text-only dropdown without flag icons
// Format: "English Name (Native Name)" for better readability when selected
export const AGENT_SEND_LANGUAGES = [
    { code: "ar", languageName: "Arabic (العربية)" },
    { code: "zh-Hans", languageName: "Chinese Simplified (中文)" },
    { code: "en", languageName: "English" },
    { code: "fr", languageName: "French (Français)" },
    { code: "de", languageName: "German (Deutsch)" },
    { code: "it", languageName: "Italian (Italiano)" },
    { code: "ja", languageName: "Japanese (日本語)" },
    { code: "ko", languageName: "Korean (한국어)" },
    { code: "pl", languageName: "Polish (Polski)" },
    { code: "pt", languageName: "Portuguese (Português)" },
    { code: "ru", languageName: "Russian (Русский)" },
    { code: "es", languageName: "Spanish (Español)" },
    { code: "vi", languageName: "Vietnamese (Tiếng Việt)" },
];

// Local storage key for translation preferences
export const TRANSLATION_STORAGE_KEY = "blitzz_translation_preferences";

// Default translation settings
export const DEFAULT_TRANSLATION_SETTINGS = {
    isAutoTranslateEnabled: false,
    selectedLanguageCode: "en",
};

/**
 * Get Azure language code from app locale code
 * @param {string} localeCode - App locale code (e.g., 'en_US')
 * @returns {string} Azure language code (e.g., 'en')
 */
export const getAzureLanguageCode = (localeCode) => {
    return LOCALE_TO_AZURE_LANGUAGE_MAP[localeCode] || "en";
};

/**
 * Get translation language object by code
 * @param {string} code - Azure language code
 * @returns {object|undefined} Language object
 */
export const getTranslationLanguageByCode = (code) => {
    return TRANSLATION_LANGUAGES.find((lang) => lang.code === code);
};

/**
 * Get Azure language code from app's currently selected language (from call topbar)
 * @returns {string} Azure language code
 */
export const getAppLanguageCode = () => {
    const appLocaleCode = PublicVariables.currentLanguageObj?.languageCode;

    if (appLocaleCode && LOCALE_TO_AZURE_LANGUAGE_MAP[appLocaleCode]) {
        return LOCALE_TO_AZURE_LANGUAGE_MAP[appLocaleCode];
    }

    // Fallback to English if not found
    return "en";
};

/**
 * Get agent send language object by code
 * @param {string} code - Azure language code
 * @returns {object|undefined} Language object
 */
export const getAgentSendLanguageByCode = (code) => {
    return AGENT_SEND_LANGUAGES.find((lang) => lang.code === code);
};
